import * as THREE from '/assets/scripts/three.module.js';

export const SVG_LOCK_CLOSED = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sff-btn-svg"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;
export const SVG_LOCK_OPEN = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sff-btn-svg"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>`;
export const SVG_CUBE_GHOST = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sff-btn-svg"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>`;
export const SVG_AXIS_ROTATE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sff-btn-svg"><path d="M21.5 2v6h-6"/><path d="M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/></svg>`;
export const SVG_TRASH_REMOVE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="sff-btn-svg"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

function htmlSafe(value) {
  return String(value ?? '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
}

function finitePositive(value) {
  const n=Number(value);
  return Number.isFinite(n)&&n>0?n:0;
}

function supportTokens(value) {
  const list=Array.isArray(value)?value:String(value||'').split(/[\/,|]+/);
  return [...new Set(list.map(item=>String(item||'').trim()).filter(Boolean))];
}

function formatRadiatorSupport(caseRecord={}) {
  const support=caseRecord?.radiatorSupport && typeof caseRecord.radiatorSupport==='object' ? caseRecord.radiatorSupport : {};
  const entries=Object.entries(support)
    .map(([size,count])=>[Math.round(Number(size)||0),Math.max(1,Math.round(Number(count)||1))])
    .filter(([size])=>size>0)
    .sort((a,b)=>a[0]-b[0]);
  if(entries.length)return entries.map(([size,count])=>count>1?`${count}×${size}`:`${size}`).join(' / ');
  const max=finitePositive(caseRecord?.maxAIO);
  return max?`up to ${Math.round(max)}`:'—';
}

function zoneLabel(zone='') {
  const raw=String(zone||'').replace(/_/g,' ').trim();
  return raw ? raw.replace(/\b\w/g,ch=>ch.toUpperCase()) : 'Unknown';
}

function displayZoneForFrame(zone='',frame='physical') {
  const raw=String(zone||'').trim().toLowerCase();
  if(frame!=='reference_canonical_projected')return raw;
  const projected={top:'rear',bottom:'front',front:'top',rear:'bottom'};
  return projected[raw]||raw;
}

function fanMountText(hint={},frame='physical') {
  const counts=hint?.counts&&typeof hint.counts==='object'?hint.counts:{};
  const sizes=[...new Set([...(hint?.sizes||[]),...Object.keys(counts)].map(Number).filter(v=>Number.isFinite(v)&&v>0))].sort((a,b)=>a-b);
  const parts=sizes.map(size=>{
    const count=Math.max(1,Math.round(Number(counts[String(Math.round(size))])||1));
    const limit=finitePositive(hint?.maxFanThickness?.[String(Math.round(size))]);
    return `${count}×${Math.round(size)}${limit?` ≤${Math.round(limit)}mm`:''}`;
  });
  return `${zoneLabel(displayZoneForFrame(hint?.zone,frame))} ${parts.join(' / ')}${hint?.snapEligible===false?' · location ambiguous':''}`;
}

function radiatorMountText(hint={},frame='physical') {
  const counts=hint?.counts&&typeof hint.counts==='object'?hint.counts:{};
  const sizes=[...new Set([...(hint?.sizes||[]),...Object.keys(counts)].map(Number).filter(v=>Number.isFinite(v)&&v>0))].sort((a,b)=>a-b);
  const parts=sizes.map(size=>{
    const count=Math.max(1,Math.round(Number(counts[String(Math.round(size))])||1));
    return count>1?`${count}×${Math.round(size)}`:`${Math.round(size)}`;
  });
  const limits=[];
  const maxStack=finitePositive(hint?.maxStack);
  const maxRad=finitePositive(hint?.maxRadiatorThickness);
  if(maxStack)limits.push(`stack ≤${Math.round(maxStack)}mm`);
  if(maxRad)limits.push(`rad ≤${Math.round(maxRad)}mm`);
  if(Array.isArray(hint?.conditions)&&hint.conditions.length)limits.push(hint.conditions.map(v=>String(v).replace(/_/g,' ')).join(', '));
  return `${zoneLabel(displayZoneForFrame(hint?.zone,frame))} ${parts.join(' / ')}${hint?.snapEligible===false?' · location ambiguous':''}${limits.length?` (${limits.join(' · ')})`:''}`;
}

function fanMountSummaryText(box) {
  if(!box||box.trackType!=='fan')return '';
  const frame=Math.round(finitePositive(box.caseFanMountFrame||box.nativeSpecs?.l||box.nativeSpecs?.w)||0);
  if(box.attachedToCaseUid&&box.caseFanMountId){
    const zone=zoneLabel(box.caseFanMountZone==='side'&&box.caseFanMountFace?box.caseFanMountFace:(box.caseFanMountZone||box.caseFanMountFace||'case'));
    const slot=Number(box.caseFanMountSlot);
    const count=Math.max(1,Number(box.caseFanMountCount)||1);
    return `CASE MOUNT · ${zone}${Number.isFinite(slot)?` · ${slot+1}/${count}`:''}${frame?` · ${frame}mm`:''}`;
  }
  if(box.attachedToRadiatorUid&&Number.isInteger(Number(box.assignedSlotIndex))){
    return `${box.autoFabricatedFan?'AUTO RAD FAN':'RADIATOR STACK'} · SLOT ${Number(box.assignedSlotIndex)+1}${frame?` · ${frame}mm`:''}`;
  }
  return 'LOOSE · UNSNAPPED';
}

function ramMountSummaryText(box){
  const meta=box?.componentMeta||{};
  const h=finitePositive(meta.ramHeight||box?.nativeSpecs?.h);
  const type=String(meta.memoryType||'').trim();
  const modules=Math.max(1,Math.round(Number(meta.ramKitModules||box?.ramKitModules)||2));
  const installed=Array.isArray(meta.installedRamSlots)?meta.installedRamSlots.filter(v=>Number.isFinite(Number(v))).map(Number):[];
  const total=Math.max(0,Math.round(Number(meta.motherboardRamSlots)||0));
  const slotText=installed.length?` · slots ${installed.join('/')}${total?` of ${total}`:''}`:(total?` · ${total} board slots`:'');
  return `RAM ${modules}× DIMM · ${h?`${Math.round(h*10)/10}mm high`:'height unknown'}${type?` · ${type}`:''}${meta.rgb?' · RGB':''}${slotText} · board placement approximate`;
}

function buildComponentSupportSummary(box) {
  if(!box?.isComponent)return '';
  const meta=box.componentMeta||{};
  if(box.trackType==='fan'){
    return `<div id="fan_mount_summary_${htmlSafe(box.uid)}" style="font-size:9px;color:var(--sub-text);margin-top:1px;font-weight:700;letter-spacing:.02em;">${htmlSafe(fanMountSummaryText(box))}</div>`;
  }
  if(box.trackType==='radiator'){
    const tube=finitePositive(meta.tubeLength);
    const tubeText=tube?(meta.tubeLengthKnown?`Tube ${Math.round(tube)}mm`:`Tube ~${Math.round(tube)}mm default`):'Tube unknown';
    const block=meta.aioBlock||null;
    const blockText=block&&finitePositive(block.h)?`Block ${Math.round(finitePositive(block.l)||70)}×${Math.round(finitePositive(block.w)||70)}×${Math.round(block.h)}mm`:'';
    return `<div style="font-size:9px;color:var(--sub-text);margin-top:1px;">${htmlSafe([tubeText,blockText].filter(Boolean).join(' · '))}</div>`;
  }
  if(box.trackType==='ram'){
    return `<div id="ram_mount_summary_${htmlSafe(box.uid)}" style="font-size:9px;color:var(--sub-text);margin-top:1px;">${htmlSafe(ramMountSummaryText(box))}</div>`;
  }
  if(box.trackType==='motherboard'){
    const slots=Math.max(0,Math.round(Number(meta.ramSlots)||0));
    const ramType=String(meta.ramType||'').trim();
    return slots?`<div style="font-size:9px;color:var(--sub-text);margin-top:1px;">RAM ${slots} slots${ramType?` · ${htmlSafe(ramType)}`:''} · DIMM guides approximate</div>`:'';
  }
  if(box.trackType==='aircooler'){
    const h=finitePositive(box.nativeSpecs?.h);
    const clearance=finitePositive(meta.ramClearance);
    const ramText=meta.ramClearanceOpen?'RAM no limit':(clearance?`RAM \u2264${Math.round(clearance*10)/10}mm`:'RAM clearance unknown');
    const type=String(meta.coolerType||'').trim();
    return `<div style="font-size:9px;color:var(--sub-text);margin-top:1px;">${h?`${Math.round(h*10)/10}mm cooler \u00b7 `:''}${htmlSafe(ramText)}${type?` \u00b7 ${htmlSafe(type)}`:''}</div>`;
  }
  return '';
}

function buildCaseSupportSummary(box) {
  if(!box?.isCase)return '';
  const c=box.catalogRecord||{};
  const evidence=box.spatialEvidence||c.spatialHints||{};
  const mobos=supportTokens(c.supportedMobo?.length?c.supportedMobo:c.mobo);
  const psus=supportTokens(c.supportedPSU?.length?c.supportedPSU:c.psu);
  const gpu=[finitePositive(c.maxGPU),finitePositive(c.maxThick),finitePositive(c.maxGPUHeight)];
  const cpu=finitePositive(c.maxCPU);
  const rad=formatRadiatorSupport(c);
  const fanHints=Array.isArray(evidence?.fans)?evidence.fans:[];
  const radHints=Array.isArray(evidence?.radiators)?evidence.radiators:[];
  const evidenceFrame=String(box.spatialEvidenceFrame||'physical');
  const specRows=[];
  if(mobos.length)specRows.push(`<span><b>MB</b> ${htmlSafe(mobos.join(' · '))}</span>`);
  if(psus.length)specRows.push(`<span><b>PSU</b> ${htmlSafe(psus.join(' · '))}</span>`);
  if(gpu.some(Boolean))specRows.push(`<span><b>GPU</b> ${gpu[0]?Math.round(gpu[0]):'?'}L × ${gpu[1]?Math.round(gpu[1]):'?'}T × ${gpu[2]?Math.round(gpu[2]):'?'}H mm</span>`);
  if(cpu)specRows.push(`<span><b>CPU</b> ${Math.round(cpu)}mm</span>`);
  if(rad!=='—')specRows.push(`<span><b>RAD</b> ${htmlSafe(rad)}mm</span>`);
  const mountRows=[];
  if(fanHints.length)mountRows.push(`<div><span style="color:#a78bfa;font-weight:800;">FAN</span> ${htmlSafe(fanHints.map(hint=>fanMountText(hint,evidenceFrame)).join(' · '))}</div>`);
  if(radHints.length)mountRows.push(`<div><span style="color:#34d399;font-weight:800;">RAD</span> ${htmlSafe(radHints.map(hint=>radiatorMountText(hint,evidenceFrame)).join(' · '))}</div>`);
  if(!specRows.length&&!mountRows.length)return '';
  return `<div class="sff-case-support-summary" style="margin-top:4px;padding:5px 6px;border:1px solid var(--sidebar-border);border-radius:4px;background:var(--input-bg);font-size:8.5px;line-height:1.45;color:var(--sub-text);">
    <div style="font-size:8px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;margin-bottom:3px;color:var(--main-text);">CASE SUPPORT</div>
    ${specRows.length?`<div style="display:flex;flex-wrap:wrap;gap:3px 9px;">${specRows.join('')}</div>`:''}
    ${mountRows.length?`<div style="margin-top:4px;padding-top:4px;border-top:1px dashed var(--sidebar-border);">${mountRows.join('')}</div>`:''}
  </div>`;
}

export function createManifestUi({ getActiveBoxes, getViewport, getCamera, buildCaseSpatialControls } = {}) {
  const projectionVector = new THREE.Vector3();

  function updateMobileManifestCountBadge() {
    const countEl = document.getElementById('mobile-manifest-count');
    if (countEl) countEl.textContent = (getActiveBoxes?.() || []).length;
  }

  function checkEmptyState() {
    const list = document.getElementById('active-cases-list');
    if (!list) return;
    const activeBoxes=getActiveBoxes?.() || [];
    if (!activeBoxes.length) {
      list.innerHTML = '<li id="empty-notice" style="padding:12px 0; color:var(--sub-text); text-align:center; font-style:italic; font-size:9.5px; list-style:none !important;" class="font-mono">No components added.</li>';
    } else {
      document.getElementById('empty-notice')?.remove();
    }
    updateMobileManifestCountBadge();
  }

  function appendDomManifestRow(box, labelName, dimensionStr, assignedColor, isComp) {
    const targetList = document.getElementById('active-cases-list');
    if (!targetList) return;
    document.getElementById('empty-notice')?.remove();
    const activeBoxes=getActiveBoxes?.() || [];

    const li = document.createElement('li');
    li.className = 'manifest-item font-mono';
    li.id = `item_${box.uid}`;
    const parts = String(dimensionStr || '').replace(/ mm/g, '').split(' x ').map(Number);
    const l = parts[0]||0, w = parts[1]||0, h = parts[2]||0;

    const volumeDisplay = isComp ? '' : `<div style="font-size:10.5px; margin-left:0px; color:var(--sub-text); margin-top:2px; font-weight:600; white-space:normal;">${((l * w * h) / 1000000).toFixed(1)}L • Desk Space: ${Math.round((l * w) / 100)} cm²</div>`;
    const caseSupportSummary = isComp ? '' : buildCaseSupportSummary(box);
    const componentSupportSummary = isComp ? buildComponentSupportSummary(box) : '';
    const xRayBtn = isComp ? '' : `<button onclick="toggleXRayMode('${box.uid}', this)" style="background:none; border:none; color:var(--sub-text); cursor:pointer; font-size:10.5px; padding:0; display:inline-flex; align-items:center; gap:4px;" class="font-mono">${SVG_CUBE_GHOST} <span>Ghost</span></button>`;

    let lockToggleButton = '';
    if (isComp) {
      const linkedFan=box.trackType==='fan' && box.attachedToRadiatorUid && !box.fanAssemblyDetached;
      lockToggleButton = box.trackType==='ram'
        ? `<span style="color:#c084fc;font-size:10.5px;display:inline-flex;align-items:center;gap:4px;">${SVG_LOCK_CLOSED} <span>Board Mount</span></span>`
        : `<button id="lock_btn_${box.uid}" onclick="window.toggleComponentLock('${box.uid}', this)" style="background:none; border:none; color:${linkedFan?'#38bdf8':'var(--sub-text)'}; cursor:pointer; font-size:10.5px; padding:0; display:inline-flex; align-items:center; gap:4px;" class="font-mono">${linkedFan?SVG_LOCK_CLOSED:SVG_LOCK_OPEN} <span>${linkedFan?'Stack':'Unlocked'}</span></button>`;
    } else {
      lockToggleButton = `<button id="master_lock_btn_${box.uid}" onclick="window.toggleCaseMasterLock('${box.uid}', this)" style="background:none; border:none; color:var(--sub-text); cursor:pointer; font-size:10.5px; padding:0; display:inline-flex; align-items:center; gap:4px;" class="font-mono">${SVG_LOCK_OPEN} <span>Lock All</span></button>`;
    }

    const spatialCaseControls = isComp ? '' : (buildCaseSpatialControls?.(box) || '');
    const snapControl = !isComp
      ? `<button onclick="snapAllComponentsToCase('${box.uid}')" title="Return all solver-backed hardware to this case's current topology" style="background:none;border:none;color:var(--sub-text);cursor:pointer;font-size:10.5px;padding:0;display:inline-flex;align-items:center;gap:4px;" class="font-mono"><span>Snap All</span></button>`
      : box.trackType==='ram'
        ? `<button onclick="snapRamToMotherboard('${box.uid}')" title="Return this RAM kit to the motherboard DIMM bank" style="background:none;border:none;color:var(--sub-text);cursor:pointer;font-size:10.5px;padding:0;display:inline-flex;align-items:center;gap:4px;" class="font-mono"><span>Snap</span></button>`
        : box.isAirCooler ? ''
        : box.trackType==='fan' && box.autoFabricatedFan
          ? `<button id="fan_replace_${box.uid}" onclick="armAutoFanReplacement('${box.uid}')" title="Replace this auto fan with the next manually added fan" style="background:none;border:none;color:var(--sub-text);cursor:pointer;font-size:10.5px;padding:0;display:inline-flex;align-items:center;gap:4px;" class="font-mono"><span>Replace</span></button>`
          : `<button onclick="cycleComponentSnap('${box.uid}',1)" style="background:none;border:none;color:var(--sub-text);cursor:pointer;font-size:10.5px;padding:0;display:inline-flex;align-items:center;gap:4px;" class="font-mono"><span>Snap</span></button>`;

    let alignmentSliders = '';
    if (isComp && box.trackType!=='ram') {
      const hasMobo = activeBoxes.some(item => item.isComponent && item.uid && item.uid.toString().includes('mobo'));
      const initX = (box.isAirCooler && hasMobo) ? (box.userOffsetX || 0) : box.position.x;
      const initY = (box.isAirCooler && hasMobo) ? (box.userOffsetY || 0) : (box.position.y - (box.totalModelHeight / 2));
      const initZ = (box.isAirCooler && hasMobo) ? (box.userOffsetZ || 0) : box.position.z;

      alignmentSliders = `
      <div style="margin-top: 6px; display: flex; flex-direction: column; gap: 3px; width: 100%; border-top:1px dashed var(--sidebar-border); padding-top:4px;">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
              <span style="font-size: 9px; color: var(--sub-text); min-width:95px; font-weight:700; text-transform:uppercase;">Left / Right</span>
              <button class="sff-micro-btn font-mono" onclick="stepComponentAxis('${box.uid}','x',-1)">-</button>
              <input type="range" min="-600" max="600" value="${initX}" step="0.1" class="sff-x-range-node sff-camera-range" oninput="adjustComponentX('${box.uid}', this.value)" style="flex:1; height:3px;" />
              <button class="sff-micro-btn font-mono" onclick="stepComponentAxis('${box.uid}','x',1)">+</button>
              <span id="xval_${box.uid}" style="font-size: 9px; color: var(--sub-text); min-width:32px; text-align:right;">${initX.toFixed(1)}mm</span>
          </div>
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
              <span style="font-size: 9px; color: var(--sub-text); min-width:95px; font-weight:700; text-transform:uppercase;">Down / Up</span>
              <button class="sff-micro-btn font-mono" onclick="stepComponentAxis('${box.uid}','y',-1)">-</button>
              <input type="range" min="-400" max="400" value="${initY}" step="0.1" class="sff-y-range-node sff-camera-range" oninput="adjustComponentHeight('${box.uid}', this.value)" style="flex:1; height:3px;" />
              <button class="sff-micro-btn font-mono" onclick="stepComponentAxis('${box.uid}','y',1)">+</button>
              <span id="hval_${box.uid}" style="font-size: 9px; color: var(--sub-text); min-width:32px; text-align:right;">${initY.toFixed(1)}mm</span>
          </div>
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
              <span style="font-size: 9px; color: var(--sub-text); min-width:95px; font-weight:700; text-transform:uppercase;">Back / Forward</span>
              <button class="sff-micro-btn font-mono" onclick="stepComponentAxis('${box.uid}','z',-1)">-</button>
              <input type="range" min="-600" max="600" value="${initZ}" step="0.1" class="sff-z-range-node sff-camera-range" oninput="adjustComponentZ('${box.uid}', this.value)" style="flex:1; height:3px;" />
              <button class="sff-micro-btn font-mono" onclick="stepComponentAxis('${box.uid}','z',1)">+</button>
              <span id="zval_${box.uid}" style="font-size: 9px; color: var(--sub-text); min-width:32px; text-align:right;">${initZ.toFixed(1)}mm</span>
          </div>
      </div>`;
    }

    const orientControl = box.trackType==='ram' ? '' : `<button onclick="flipCaseOrientation('${box.uid}')" style="background:none; border:none; color:var(--sub-text); cursor:pointer; font-size:10.5px; padding:0; display:inline-flex; align-items:center; gap:4px;" class="font-mono">${SVG_AXIS_ROTATE} <span>Orient</span></button>`;
    const databaseBridgeControl = !isComp
      ? `<button onclick="openSffBuildInDatabase()" title="Open this exact catalog-ID build in /db" style="background:none;border:none;color:var(--sub-text);cursor:pointer;font-size:10.5px;padding:0;display:inline-flex;align-items:center;gap:4px;" class="font-mono"><span>Open /DB</span></button>`
      : '';

    li.innerHTML = `
      <div style="display:flex; flex-direction:column; width: 100%; gap:6px;">
          <div style="display:flex; align-items:center; gap:6px; width:100%;">
              <span style="display:inline-block; width:6px; height:6px; border-radius:50%; background-color:#${assignedColor.toString(16).padStart(6, '0')}; flex-shrink:0;"></span>
              <span style="font-weight:700; font-size:10.5px; color:var(--main-text); word-break:break-word; white-space:normal; line-height:1.3;">${labelName}</span>
          </div>
          ${volumeDisplay}
          ${componentSupportSummary}
          ${caseSupportSummary}
          ${spatialCaseControls}
          ${isComp ? `<div id="placement_status_${box.uid}" style="display:none;font-size:9px;font-weight:700;line-height:1.3;"></div>` : ''}
          <div style="display:flex; flex-wrap:wrap; gap:10px; align-items:center; background:var(--input-bg); padding:4px 8px; border-radius:4px; border:1px solid var(--sidebar-border); width:100%; box-sizing:border-box;">
              ${lockToggleButton}
              ${snapControl}
              ${xRayBtn}
              ${orientControl}
              ${databaseBridgeControl}
              <button onclick="removeModelInstance('${box.uid}')" style="background:none; border:none; color:#ef4444; font-size:10.5px; cursor:pointer; font-weight:bold; margin-left:auto; padding:0; display:inline-flex; align-items:center; gap:3px;" class="font-mono">${SVG_TRASH_REMOVE} <span>Remove</span></button>
          </div>
          ${alignmentSliders}
      </div>`;
    targetList.appendChild(li);
    updateMobileManifestCountBadge();
  }

  function updateFanMountSummary(box) {
    if(!box||box.trackType!=='fan')return;
    const el=document.getElementById(`fan_mount_summary_${box.uid}`);
    if(!el)return;
    el.textContent=fanMountSummaryText(box);
    el.style.color=box.attachedToCaseUid?'#10b981':(box.attachedToRadiatorUid?'#38bdf8':'var(--sub-text)');
  }

  function updateRamMountSummary(box) {
    if(!box||box.trackType!=='ram')return;
    const el=document.getElementById(`ram_mount_summary_${box.uid}`);
    if(!el)return;
    el.textContent=ramMountSummaryText(box);
    el.style.color=box.attachedToMoboUid?'#c084fc':'#f59e0b';
  }

  function attachHtmlBadgeNode(box, labelString) {
    const viewport=getViewport?.();
    if (!viewport || !box) return;
    const badge = document.createElement('div');
    badge.className = 'sff-3d-badge font-mono';
    badge.textContent = labelString;
    viewport.appendChild(badge);
    box.badgeEl = badge;
  }

  function updateFloatingBadges() {
    const viewport=getViewport?.();
    const camera=getCamera?.();
    if (!viewport || !camera) return;
    const viewportWidth = viewport.clientWidth;
    const viewportHeight = viewport.clientHeight;
    for (const box of getActiveBoxes?.() || []) {
      if (!box.badgeEl) continue;
      box.updateMatrixWorld();
      projectionVector.setFromMatrixPosition(box.matrixWorld).project(camera);
      if (projectionVector.z <= 1) {
        const x=(projectionVector.x * .5 + .5) * viewportWidth;
        const y=(projectionVector.y * -.5 + .5) * viewportHeight;
        box.badgeEl.style.display = 'block';
        box.badgeEl.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -100%)`;
      } else {
        box.badgeEl.style.display = 'none';
      }
    }
  }

  return { updateMobileManifestCountBadge, checkEmptyState, appendDomManifestRow, updateFanMountSummary, updateRamMountSummary, attachHtmlBadgeNode, updateFloatingBadges };
}
